import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Badge, Button, Card, Counters, EmptyState, SearchInput, Select, Skel, SkeletonRows, Sym,
} from '../../components/app2';
import { strapiService } from '../../utils/api';
import billingService from '../../utils/billingService';

/**
 * Prospectos captados por el asistente de WhatsApp (rediseño jul-2026).
 *
 * Mismos filtros (estado, temperatura, búsqueda y la vista por defecto "solo
 * sin conversión" que pidió negocio), mismos contadores y la misma navegación
 * a la ficha. Cambia el molde: barra de contadores del sistema, tabla con
 * tarjetas en móvil y estados/temperatura con las etiquetas del rediseño.
 *
 * Dos mejoras de lectura:
 *  - El plan de interés se muestra con el nombre del catálogo ("De 21 a 50
 *    trabajadores") en vez del identificador interno ("tier-50"). El nombre se
 *    toma del propio catálogo de planes, no de una lista escrita a mano, así
 *    que sigue al día si se renombra un plan.
 *  - Las filas se pueden abrir con el teclado (Enter o Espacio): antes solo
 *    respondían al clic.
 */

const STATUS_LABEL = {
  new: 'Nuevo',
  contacted: 'Contactado',
  qualified: 'Calificado',
  escalated: 'Escalado',
  converted: 'Convertido',
  lost: 'Perdido',
};

/* Tonos del sistema para cada estado del embudo. */
const STATUS_TONE = {
  new: 'brand',
  contacted: 'brand',
  qualified: 'warn',
  escalated: 'warn',
  converted: 'ok',
  lost: 'neutral',
};

const STATUS_ICON = {
  new: 'fiber_new',
  contacted: 'forum',
  qualified: 'verified',
  escalated: 'priority_high',
  converted: 'task_alt',
  lost: 'do_not_disturb_on',
};

const TEMP_LABEL = { unevaluated: 'Sin evaluar', cold: 'Frío', warm: 'Tibio', hot: 'Caliente' };
const TEMP_TONE = { unevaluated: 'neutral', cold: 'brand', warm: 'warn', hot: 'danger' };
const TEMP_ICON = {
  unevaluated: 'help',
  cold: 'ac_unit',
  warm: 'device_thermostat',
  hot: 'local_fire_department',
};

const STATUS_OPTIONS = [
  { value: '', label: 'Todos los estados' },
  ...Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })),
];
const TEMP_OPTIONS = [
  { value: '', label: 'Todas las temperaturas' },
  ...Object.entries(TEMP_LABEL).map(([value, label]) => ({ value, label })),
];
const PAGE_SIZE = 25;

const maskPhone = (p) => (p && p.length > 6 ? `${p.slice(0, 4)}···${p.slice(-3)}` : p || '—');
const fmtDate = (d) =>
  d ? new Date(d).toLocaleString('es-MX', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—';

/* Cinco columnas + acceso a la ficha: 760 + 70 de separaciones + 40 de padding. */
const GRID = 'minmax(210px,1.6fr) 132px 128px 130px 160px 60px';
const GRID_GAP = 14;
const GRID_MIN = 870;

const AdminProspects = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [statsLoading, setStatsLoading] = useState(true);
  const [prospects, setProspects] = useState([]);
  const [meta, setMeta] = useState(null);
  const [stats, setStats] = useState(null);
  const [page, setPage] = useState(1);
  // Vista default pedida por negocio: "sin conversión".
  const [unconverted, setUnconverted] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [tempFilter, setTempFilter] = useState('');
  const [search, setSearch] = useState('');
  const [planNames, setPlanNames] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, pageSize: PAGE_SIZE };
      if (unconverted) params.unconverted = 'true';
      if (statusFilter) params.status = statusFilter;
      if (tempFilter) params.temperature = tempFilter;
      if (search.trim()) params.search = search.trim();
      const res = await strapiService.prospects.list(params);
      setProspects(res?.data || []);
      setMeta(res?.meta?.pagination || null);
    } catch (e) {
      console.error('Error cargando prospectos:', e);
      setProspects([]);
    } finally {
      setLoading(false);
    }
  }, [page, unconverted, statusFilter, tempFilter, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    (async () => {
      setStatsLoading(true);
      try { setStats(await strapiService.prospects.stats()); }
      catch (e) { console.error('Error cargando stats:', e); }
      finally { setStatsLoading(false); }
    })();
  }, []);

  // Catálogo de planes: solo para traducir el identificador del plan de interés
  // ("tier-50") al nombre que ve el cliente. Si falla, se muestra el id tal cual.
  useEffect(() => {
    (async () => {
      try {
        const res = await billingService.listPlans();
        const porSlug = {};
        (Array.isArray(res?.data) ? res.data : []).forEach((p) => {
          if (p?.slug) porSlug[p.slug] = p.name;
        });
        setPlanNames(porSlug);
      } catch (e) {
        console.error('Error cargando catálogo de planes:', e);
      }
    })();
  }, []);

  // reset de página al cambiar filtros
  useEffect(() => { setPage(1); }, [unconverted, statusFilter, tempFilter, search]);

  const nombrePlan = (slug) => (slug ? (planNames[slug] || slug) : '—');

  const contadores = [
    { label: 'Total', value: stats?.total ?? 0 },
    { label: 'Nuevos', value: stats?.nuevos ?? 0, tone: 'brand' },
    { label: 'Calificados', value: stats?.calificados ?? 0, tone: 'warn' },
    { label: 'Escalados', value: stats?.escalados ?? 0, tone: 'warn' },
    { label: 'Convertidos', value: stats?.convertidos ?? 0, tone: 'ok' },
    { label: 'Perdidos', value: stats?.perdidos ?? 0, tone: 'muted' },
    { label: 'Conversión', value: `${stats?.conversionRate ?? 0}%`, tone: 'brand' },
  ];

  const hayFiltros = Boolean(search || statusFilter || tempFilter);

  const abrirFicha = (p) => navigate(`/app/admin/prospects/${p.id}`);

  /** Fila/tarjeta accionable: clic, Enter y Espacio abren la ficha. */
  const filaProps = (p) => ({
    role: 'button',
    tabIndex: 0,
    'aria-label': `Abrir ficha de ${p.name || 'prospecto sin nombre'}`,
    onClick: () => abrirFicha(p),
    onKeyDown: (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        abrirFicha(p);
      }
    },
  });

  const Contacto = ({ p }) => (
    <div style={{ minWidth: 0 }}>
      <div className="a2-tcell-strong a2-truncate" title={p.name || '(sin nombre)'}>
        {p.name || '(sin nombre)'}
      </div>
      <div className="a2-tcell-muted a2-truncate">
        <span className="a2-mono">{maskPhone(p.phone)}</span>
        {p.email ? ` · ${p.email}` : ''}
      </div>
    </div>
  );

  const EstadoBadge = ({ p }) => (
    <Badge tone={STATUS_TONE[p.status] || 'neutral'} icon={STATUS_ICON[p.status] || 'label'}>
      {STATUS_LABEL[p.status] || p.status}
    </Badge>
  );

  const TempBadge = ({ p }) => (
    <Badge tone={TEMP_TONE[p.temperature] || 'neutral'} icon={TEMP_ICON[p.temperature] || 'help'}>
      {TEMP_LABEL[p.temperature] || p.temperature}
    </Badge>
  );

  return (
    <div className="a2-anim-screen">
      <Helmet>
        <title>Prospectos - PaySlip</title>
        <meta
          name="description"
          content="Contactos captados por el asistente de WhatsApp: cualificación y conversión"
        />
      </Helmet>

      {statsLoading && !stats ? (
        <div className="a2-counters" style={{ marginBottom: 16 }}>
          {contadores.map((c) => (
            <div key={c.label}>
              <Skel w={48} h={26} />
              <Skel w={70} h={11} style={{ marginTop: 6 }} />
            </div>
          ))}
        </div>
      ) : (
        <Counters items={contadores} className="a2-anim-fadein" style={{ marginBottom: 16 }} />
      )}

      <Card pad={false}>
        <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--line)' }}>
          <div style={{ marginBottom: 14, minWidth: 0 }}>
            <h3 className="a2-card-title">Prospectos</h3>
            <p className="a2-card-sub">
              Contactos captados por el asistente de WhatsApp — cualificación y conversión
            </p>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e?.target?.value)}
              placeholder="Nombre, teléfono o correo…"
              aria-label="Buscar prospecto"
              style={{ flex: 1, minWidth: 200, padding: '9px 12px', borderRadius: 9, background: 'var(--surface-2)' }}
            />
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e?.target?.value)}
              aria-label="Filtrar por estado"
              style={{ width: 'auto', minWidth: 175, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <Select
              value={tempFilter}
              onChange={(e) => setTempFilter(e?.target?.value)}
              aria-label="Filtrar por temperatura"
              style={{ width: 'auto', minWidth: 190, padding: '9px 12px', borderRadius: 9, fontSize: '.88rem' }}
            >
              {TEMP_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
            </Select>
            <label className="a2-check" style={{ whiteSpace: 'nowrap' }}>
              <input
                type="checkbox"
                checked={unconverted}
                onChange={(e) => setUnconverted(e.target.checked)}
              />
              Solo sin conversión
            </label>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 20 }}>
            <SkeletonRows rows={6} title={false} />
          </div>
        ) : prospects.length === 0 ? (
          <EmptyState
            icon={hayFiltros ? 'search_off' : 'chat'}
            title={hayFiltros ? 'Sin coincidencias' : 'Todavía no hay prospectos'}
            text={hayFiltros
              ? 'Ajusta la búsqueda o los filtros de estado y temperatura.'
              : 'Cuando el asistente de WhatsApp capte contactos, aparecerán aquí.'}
            className="a2-card"
            style={{ border: 'none' }}
          />
        ) : (
          <>
            {/* Escritorio */}
            <div className="a2-only-lg">
              <div style={{ overflowX: 'auto' }}>
                <div style={{ minWidth: GRID_MIN }}>
                  <div className="a2-thead" style={{ gridTemplateColumns: GRID, gap: GRID_GAP }}>
                    <div>Prospecto</div>
                    <div>Estado</div>
                    <div>Temperatura</div>
                    <div>Plan de interés</div>
                    <div>Último contacto</div>
                    <div style={{ textAlign: 'right' }}>Ficha</div>
                  </div>

                  {prospects.map((p) => (
                    <div
                      key={p.id}
                      className="a2-trow"
                      style={{ gridTemplateColumns: GRID, gap: GRID_GAP, cursor: 'pointer' }}
                      {...filaProps(p)}
                    >
                      <Contacto p={p} />
                      <div><EstadoBadge p={p} /></div>
                      <div><TempBadge p={p} /></div>
                      <div className="a2-tcell">{nombrePlan(p.interested_tier)}</div>
                      <div className="a2-tcell">{fmtDate(p.last_contact_at || p.createdAt)}</div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Sym name="chevron_right" size={20} style={{ color: 'var(--faint)' }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Móvil */}
            <div className="a2-only-sm">
              {prospects.map((p) => (
                <div
                  key={p.id}
                  style={{ padding: '14px 16px', borderBottom: '1px solid var(--line)', cursor: 'pointer' }}
                  {...filaProps(p)}
                >
                  <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
                    <Contacto p={p} />
                    <Sym name="chevron_right" size={20} style={{ color: 'var(--faint)', flexShrink: 0 }} />
                  </div>

                  <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                    <EstadoBadge p={p} />
                    <TempBadge p={p} />
                  </div>

                  <div
                    style={{
                      marginTop: 10, display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10,
                    }}
                  >
                    <div>
                      <div className="a2-tcell-muted">Plan de interés</div>
                      <div className="a2-tcell">{nombrePlan(p.interested_tier)}</div>
                    </div>
                    <div>
                      <div className="a2-tcell-muted">Último contacto</div>
                      <div className="a2-tcell">{fmtDate(p.last_contact_at || p.createdAt)}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {meta && meta.pageCount > 1 ? (
          <div
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 10, padding: '14px 20px', borderTop: '1px solid var(--line)',
            }}
          >
            <Button
              variant="outline"
              size="sm"
              icon="chevron_left"
              disabled={page <= 1}
              onClick={() => setPage((p) => p - 1)}
            >
              Anterior
            </Button>
            <span className="a2-tcell-muted">
              {meta.total} prospecto{meta.total !== 1 ? 's' : ''} · página {meta.page} de {meta.pageCount}
            </span>
            <Button
              variant="outline"
              size="sm"
              iconRight="chevron_right"
              disabled={page >= meta.pageCount}
              onClick={() => setPage((p) => p + 1)}
            >
              Siguiente
            </Button>
          </div>
        ) : null}
      </Card>
    </div>
  );
};

export default AdminProspects;
